// Lib
import React, { useRef } from 'react';
import classNames from 'classnames';

// Utils
import useComputedBackgroundColor from '../../../../../../utils/dom/useComputedBackgroundColor';
import { getIsLowContrastSwatch, isEmptyColorValue, resolveColorData } from '../colorButtonUtils';

// Components
import Button from '../../../../../../components/buttons/Button';
import DualColorIcon from '../DualColorIcon';

// Constants
import { COLOR_POPUP_MODE } from './colorPopupConstants';
import { Color } from '../../../../../../../common/colors/colorConstants';

// Styles
import './ColorButton.scss';

/** The custom property ColorButton.scss paints the swatch fill from, in both render modes. */
const COLOR_BUTTON_BACKGROUND_PROPERTY = '--ui-color-button-background';

/** The custom property the panel behind the swatch is painted from. */
const PANEL_BACKGROUND_PROPERTY = '--ui-modal-background-primary';

export type ColorButtonProps = {
    selectedType: string;
    selectedStatus: string | null;
    className?: string;
    colorPopupMode?: string;
    color: string | Color | null;
    secondaryColor?: string;
    isActive: boolean;
    isContextMenu?: boolean;
    isDarkModeTheme: boolean;
    setColor: (color: string | null, secondaryColor?: string | null) => void;
    disabled?: boolean;
    preventUpdateSecondaryColor?: boolean;
};

const ColorButton = ({
    color,
    secondaryColor,
    isActive,
    setColor,
    selectedType,
    selectedStatus,
    isContextMenu,
    className,
    colorPopupMode,
    isDarkModeTheme,
    preventUpdateSecondaryColor,
}: ColorButtonProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const { colorCss, colorName } = resolveColorData(color);
    const shouldResolveComputedColor = !colorCss && !isEmptyColorValue(color);
    const computedBackgroundColor = useComputedBackgroundColor(shouldResolveComputedColor ? buttonRef : null);

    const onColorClick = () => {
        if (!setColor) return;

        // For scenarios like updating the color bar, ensure the secondary color is not updated
        if (preventUpdateSecondaryColor) {
            setColor(colorName);
            return;
        }

        // Update secondary to the provided secondaryColor or null, undefined will NOT be updated
        setColor(colorName, secondaryColor || null);
    };

    const displayColorCss = computedBackgroundColor || colorCss;

    // Both colours are read off this button, so the comparison follows whatever the cascade
    // resolved for it: the current theme, a custom board background, or a locally recoloured
    // panel. Reading the swatch from its custom property rather than the element background also
    // covers colour bar mode, where the fill is drawn by a pseudo element.
    const swatchColor = useComputedBackgroundColor(buttonRef, COLOR_BUTTON_BACKGROUND_PROPERTY);
    const surfaceColor = useComputedBackgroundColor(buttonRef, PANEL_BACKGROUND_PROPERTY);

    const classes = classNames('ColorButton', className, selectedType, selectedStatus, colorPopupMode, {
        active: isActive,
        'no-color': !displayColorCss,
        'low-contrast-color': getIsLowContrastSwatch(swatchColor, surfaceColor),
        'remove-color': !colorCss && colorPopupMode === COLOR_POPUP_MODE.COLOR_BAR,
    });

    return (
        <Button
            ref={buttonRef}
            className={classes}
            style={colorCss ? ({ [COLOR_BUTTON_BACKGROUND_PROPERTY]: colorCss } as React.CSSProperties) : undefined}
            onClickFn={onColorClick}
            onContextMenu={isContextMenu ? onColorClick : undefined}
            onMouseUp={isContextMenu ? onColorClick : undefined}
        >
            {secondaryColor && <DualColorIcon secondaryColor={secondaryColor} selectedType={selectedType} />}
            <div className="selection-border" />
        </Button>
    );
};

export default ColorButton;
